<template>
  <div v-if="!$apollo.loading">
    <FlowHeading
      :title="$t('Add your business details')"
      :sub-title="
        $t('This information allows us to show your correct business information on your bills.')
      "
    />

    <SgBusinessEntityDetailsForm
      v-model:business-entity="accountData.primaryBusinessEntity"
      :compliance-data="{}"
      :errors-graphql="errors"
    />

    <div class="wds-input-group tw-mt-6 tw-mb-4">
      <WdsButton full-width lg primary :disabled="!canSubmit" @click="callNextScreen">
        <div class="tw-flex">
          <transition name="fade">
            <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
            <ButtonSpinner v-if="isSubmitting" size="sm" color="blue" />
          </transition>
          {{ $t('Continue') }}
        </div>
      </WdsButton>
    </div>
    <div v-if="lacksRequiredFields" class="tw-mt-4">
      <WdsStack justify-center>
        <WdsButtonGhost secondary @click="skip">{{ $t('Skip and update later') }}</WdsButtonGhost>
      </WdsStack>
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { WdsButton, WdsButtonGhost, WdsStack } from '@worksome/wds-components'
import { ref } from 'vue'
import SgBusinessEntityDetailsForm from '@/components/compliances/BusinessEntity/Sg/SgBusinessEntityDetailsForm.vue'
import { UpdatePrimaryBusinessEntityMutation } from '@/components/compliances/mutations/UpdatePrimaryBusinessEntityMutation'
import { ActiveAccountBusinessEntity } from '@/components/compliances/queries/Compliances'
import businessEntityFromGraphql from '@/components/compliances/utils/graphql/businessEntityFromGraphql'
import FlowHeading from '@/components/flows/FlowHeading.vue'
import { FormValidation } from '@/utils/formValidation'

export default {
  name: 'SgBusinessEntityTypeDetails',

  components: {
    SgBusinessEntityDetailsForm,
    FlowHeading,
    WdsButton,
    WdsButtonGhost,
    WdsStack,
  },

  inject: ['skip', 'next'],

  props: {
    entityType: {
      type: String,
      required: true,
    },
  },

  setup() {
    const v$ = useVuelidate()
    const errors = ref({})

    return {
      v$,
      errors,
    }
  },

  data() {
    return {
      accountData: null,
      isSubmitting: false,
    }
  },

  computed: {
    lacksRequiredFields() {
      return this.v$.$invalid
    },
    canSubmit() {
      return !this.isSubmitting
    },
  },

  methods: {
    async callNextScreen() {
      this.errors = {}
      this.v$.$touch()
      if (this.v$.$invalid) {
        return
      }
      this.isSubmitting = true
      await this.updateBusinessEntityDetails()
      if (Object.keys(this.errors).length === 0) {
        this.next()
      } else {
        this.isSubmitting = false
      }
    },
    async updateBusinessEntityDetails() {
      if (this.accountData === null) {
        return
      }

      const {
        globalId,
        primaryBusinessEntity: { companyName, companyNo, entityType, taxNo },
      } = this.accountData

      const input = {
        globalId,
        registered_company_name: companyName,
        business_entity: entityType,
        company_no: companyNo,
        vat_no: taxNo,
      }

      await this.$apollo
        .mutate({
          mutation: UpdatePrimaryBusinessEntityMutation,
          variables: { input },
          context: {
            headers: {
              'X-FLOW-STEP-COMPLETED': this.$route.fullPath,
            },
          },
        })
        .catch(({ graphQLErrors }) => {
          if (FormValidation.containsFormValidations(graphQLErrors)) {
            this.errors = FormValidation.fromGraphqlErrors(graphQLErrors) ?? {}
          }
        })
    },
  },

  apollo: {
    accountData: {
      query: ActiveAccountBusinessEntity,
      update(data) {
        const { primaryBusinessEntity: primaryBusinessEntityRaw, ...restActiveAccount } =
          data.me.activeAccount
        const primaryBusinessEntity = businessEntityFromGraphql({
          ...primaryBusinessEntityRaw,
          entityType: this.entityType,
        })
        return { primaryBusinessEntity, ...restActiveAccount }
      },
    },
  },
}
</script>
